import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  #showScheduled = false;
  #closeScheduled = false;

  /**
   * Resolves after the browser has painted the next frame.
   *
   * `requestAnimationFrame` runs just before a frame's paint; the nested
   * `setTimeout(0)` then runs in a task *after* that paint. Scheduling the heavy
   * dialog work here keeps it out of the tap's event handlers so it is not
   * counted as INP processing time.
   *
   * @returns {Promise<void>}
   */
  #afterNextPaint() {
    return new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    // A dialog removed from the document while open never fires `toggle`, so the scroll
    // lock it set is never released and the page stays frozen. Checked in a microtask so a
    // morph that only moves this element (disconnect + immediate reconnect) is left alone.
    const dialog = this.querySelector('dialog[scroll-lock]');
    if (!dialog?.open) return;

    queueMicrotask(() => {
      if (this.isConnected) return;
      document.documentElement.removeAttribute('scroll-lock');
      document.body.removeAttribute('scroll-lock');
    });
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#showScheduled) return;

    // `showModal()` promotes the dialog to the top layer, forces style + layout
    // of its subtree, and the `toggle` listener locks body scroll (a full-page
    // reflow). Run it after the tap has painted so it is not part of the
    // interaction's main-thread work.
    this.#showScheduled = true;

    this.#afterNextPaint().then(() => {
      this.#showScheduled = false;

      if (dialog.open) return;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      // Wait until the next tick to add the event listeners to avoid race condition
      // when `showDialog` is called within a click event listener.
      setTimeout(() => {
        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      });
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#closeScheduled) return;

    this.#closeScheduled = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Start the closing animation immediately for instant feedback. It is
    // compositor-only (transform + opacity), so the tap paints right away.
    dialog.classList.add('dialog-closing');

    // Yield past the tap's paint before the expensive work: `getAnimations()`
    // (inside onAnimationEnd) forces a style flush, and `dialog.close()` + scroll
    // unlock is a full-page reflow. Deferring them keeps them out of INP.
    await this.#afterNextPaint();

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#closeScheduled = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
          document.body.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
          document.body.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
